import React, { Suspense, useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { checkSession } from 'api/shipping'
import { BrowserRouter } from 'react-router-dom'
import {
  setHasSession,
  setOtpVerificationRequired,
  setPhoneNumForOtpVerification,
} from 'store/shipping/shippingSlice'
import GlobalStyle from 'globalStyles'
import styled from 'styled-components'

import Body from './Routes'

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
`

const SESSION_HEARTBEAT_INTERVAL =
  parseInt(process.env.REACT_APP_SESSION_HEARTBEAT_INTERVAL, 10) || 30000

function App() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isSessionChecked, setIsSessionChecked] = useState(false)
  const hasCheckedInitialSession = useRef(false)

  useEffect(() => {
    let isMounted = true

    const startSessionValidation = async () => {
      if (!hasCheckedInitialSession.current) {
        return
      }

      const isSessionValid = async () => {
        try {
          await checkSession()
        } catch (error) {
          dispatch(setHasSession(false))
        }

        if (isMounted) {
          setTimeout(isSessionValid, SESSION_HEARTBEAT_INTERVAL)
        }
      }

      isSessionValid()
    }

    const checkCurrentSession = async () => {
      try {
        const resp = await checkSession()
        if (resp.status === 200) {
          dispatch(setHasSession(true))
          if (resp.data.isOtpVerificationRequired) {
            dispatch(setOtpVerificationRequired(true))
            dispatch(setPhoneNumForOtpVerification(resp.data.phoneNumber))
          } else {
            dispatch(setOtpVerificationRequired(false))
          }
        } else {
          dispatch(setHasSession(false))
        }
      } catch (error) {
        dispatch(setHasSession(false))
      } finally {
        setIsSessionChecked(true)
        hasCheckedInitialSession.current = true
        startSessionValidation()
      }
    }

    checkCurrentSession()

    return () => {
      isMounted = false
    }
  }, [dispatch])

  if (!isSessionChecked) {
    return <LoadingContainer>{t('verification_messages.loading')}</LoadingContainer>
  }

  return (
    <BrowserRouter>
      <GlobalStyle />
      <Body />
    </BrowserRouter>
  )
}

export default function WrappedApp() {
  return (
    <Suspense fallback="...is loading">
      <App />
    </Suspense>
  )
}
